<script setup>
import { computed, ref, onMounted } from 'vue';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { CONDITION_TYPES, CONDITION_CATEGORIES, CONVERSATION_STATUSES } from '../constants';

const props = defineProps({
  condition: { type: Object, required: true },
  blockId: { type: String, required: true },
});

const emit = defineEmits(['close', 'change']);

const store = useStore();
const labels = useMapGetter('labels/getLabels');
const pipelines = useMapGetter('pipelines/getPipelines');

store.dispatch('pipelines/get');

const allStages = computed(() => {
  const list = [];
  (pipelines.value || []).forEach(pipeline => {
    (pipeline.pipeline_stages || []).forEach(stage => {
      list.push({ id: stage.id, name: `${pipeline.name} > ${stage.name}`, pipelineId: pipeline.id });
    });
  });
  return list;
});

const ct = computed(
  () => CONDITION_TYPES.find(t => t.value === props.condition.name) || {}
);

const catCondition = computed(() => {
  for (const cat of CONDITION_CATEGORIES) {
    const found = cat.conditions.find(c => c.value === props.condition.name);
    if (found) return { ...found, category: cat.label };
  }
  return { title: ct.value.label, description: '', category: '' };
});

const opts = computed(() => props.condition.options || {});
const touch = () => emit('change');

const labelSearch = ref('');

const filteredLabels = computed(() => {
  const q = labelSearch.value.toLowerCase();
  return (labels.value || []).filter(l =>
    l.title.toLowerCase().includes(q)
  );
});

const selectedLabels = computed(() => {
  const raw = opts.value.labels;
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  return raw.split(',').map(s => s.trim()).filter(Boolean);
});

const isLabelSelected = title =>
  selectedLabels.value.includes(title);

const toggleLabel = title => {
  const current = [...selectedLabels.value];
  const idx = current.indexOf(title);
  if (idx >= 0) current.splice(idx, 1);
  else current.push(title);
  props.condition.options.labels = current.join(', ');
  touch();
};

const removeLabel = title => {
  const current = selectedLabels.value.filter(l => l !== title);
  props.condition.options.labels = current.join(', ');
  touch();
};
</script>

<template>
  <Teleport to="body">
    <div class="cc-overlay" @click.self="emit('close')">
      <div class="cc-panel">
        <!-- Header with back arrow -->
        <div class="cc-header">
          <button class="cc-back" @click="emit('close')">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" stroke-width="2" stroke-linecap="round"
              stroke-linejoin="round">
              <path d="m15 18-6-6 6-6" />
            </svg>
          </button>
          <h3 class="cc-title">{{ catCondition.title }}</h3>
        </div>

        <p class="cc-description">{{ catCondition.description }}</p>

        <!-- Condition-specific fields -->
        <div class="cc-fields">
          <!-- Field-based conditions: parameter + value -->
          <template v-if="!ct.noParameter">
            <label class="cc-label">Campo / Variável</label>
            <input
              v-model="condition.options.parameter"
              type="text"
              class="cc-input"
              placeholder="Ex: [Api-request-1]status"
              @input="touch"
            />
          </template>

          <!-- Value field (equalsTo, contains, value) -->
          <template v-if="ct.valueKey && ct.valueKey !== 'labels' && ct.valueKey !== 'status'">
            <label class="cc-label">Valor esperado</label>
            <input
              v-model="condition.options[ct.valueKey]"
              type="text"
              class="cc-input"
              placeholder="Digite o valor..."
              @input="touch"
            />
          </template>

          <!-- Status selector for conversation-status-condition -->
          <template v-if="ct.valueKey === 'status'">
            <label class="cc-label">Status da conversa</label>
            <div class="cc-status-grid">
              <button
                v-for="status in CONVERSATION_STATUSES"
                :key="status"
                class="cc-status-chip"
                :class="{ 'cc-status-active': condition.options.status === status }"
                @click="condition.options.status = status; touch()"
              >
                {{ status }}
              </button>
            </div>
          </template>

          <!-- Label/tag selector -->
          <template v-if="ct.valueKey === 'labels'">
            <label class="cc-label">
              Selecione as etiquetas para verificar se existem na conversa
            </label>

            <!-- Selected labels chips -->
            <div v-if="selectedLabels.length" class="cc-selected-labels">
              <span
                v-for="label in selectedLabels"
                :key="label"
                class="cc-label-chip"
              >
                {{ label }}
                <button class="cc-chip-remove" @click="removeLabel(label)">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" stroke-width="2.5"
                    stroke-linecap="round" stroke-linejoin="round">
                    <path d="M18 6 6 18M6 6l12 12" />
                  </svg>
                </button>
              </span>
            </div>

            <!-- Search input -->
            <div class="cc-search-wrap">
              <svg class="cc-search-icon" width="14" height="14"
                viewBox="0 0 24 24" fill="none" stroke="currentColor"
                stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" />
              </svg>
              <input
                v-model="labelSearch"
                type="text"
                class="cc-search-input"
                placeholder="Buscar etiqueta..."
              />
            </div>

            <!-- Labels list -->
            <div class="cc-labels-list">
              <button
                v-for="label in filteredLabels"
                :key="label.id"
                class="cc-label-item"
                :class="{ 'cc-label-item-selected': isLabelSelected(label.title) }"
                @click="toggleLabel(label.title)"
              >
                <span
                  class="cc-label-color"
                  :style="{ background: label.color || '#6366f1' }"
                />
                <span class="cc-label-name">{{ label.title }}</span>
                <svg
                  v-if="isLabelSelected(label.title)"
                  width="16" height="16" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" stroke-width="2.5"
                  stroke-linecap="round" stroke-linejoin="round"
                  class="cc-label-check"
                >
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </button>
              <p v-if="!filteredLabels.length" class="cc-no-labels">
                Nenhuma etiqueta encontrada
              </p>
            </div>

            <p class="cc-hint">
              Será verdadeiro se a conversa possuir pelo menos uma das etiquetas informadas
            </p>
          </template>

          <!-- Pipeline selector for business-on-pipeline condition -->
          <template v-if="ct.valueKey === 'pipelineId'">
            <label class="cc-label">Pipeline / Kanban</label>
            <select
              v-model="condition.options.pipelineId"
              class="cc-input"
              @change="touch"
            >
              <option value="">Selecione o pipeline</option>
              <option
                v-for="pipeline in pipelines"
                :key="pipeline.id"
                :value="pipeline.id"
              >
                {{ pipeline.name }}
              </option>
            </select>
          </template>

          <!-- Stage selector for business-on-stage condition -->
          <template v-if="ct.valueKey === 'stageId'">
            <label class="cc-label">Etapa do Kanban</label>
            <select
              v-model="condition.options.stageId"
              class="cc-input"
              @change="touch"
            >
              <option value="">Selecione a etapa</option>
              <option
                v-for="stage in allStages"
                :key="stage.id"
                :value="stage.id"
              >
                {{ stage.name }}
              </option>
            </select>
          </template>

          <!-- Time interval condition -->
          <template v-if="condition.name === 'current-time-interval-condition'">
            <label class="cc-label">Horário de início</label>
            <div class="flex gap-2">
              <input v-model.number="condition.options.startHour" type="number" min="0" max="23" class="cc-input" placeholder="Hora" @input="touch" />
              <input v-model.number="condition.options.startMinute" type="number" min="0" max="59" class="cc-input" placeholder="Min" @input="touch" />
            </div>
            <label class="cc-label">Horário de término</label>
            <div class="flex gap-2">
              <input v-model.number="condition.options.endHour" type="number" min="0" max="23" class="cc-input" placeholder="Hora" @input="touch" />
              <input v-model.number="condition.options.endMinute" type="number" min="0" max="59" class="cc-input" placeholder="Min" @input="touch" />
            </div>
            <label class="cc-label">Fuso horário</label>
            <input v-model="condition.options.timezone" type="text" class="cc-input" placeholder="America/Sao_Paulo" @input="touch" />
          </template>

          <!-- No-config conditions -->
          <template v-if="ct.noParameter && !ct.valueKey && condition.name !== 'current-time-interval-condition'">
            <div class="cc-no-config">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" stroke-width="2" stroke-linecap="round"
                stroke-linejoin="round" class="cc-no-config-icon">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                <polyline points="22 4 12 14.01 9 11.01" />
              </svg>
              <p>Esta condição não precisa de configuração adicional.</p>
            </div>
          </template>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
.cc-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  padding: 3rem 0 3rem 2rem;
  background: rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(2px);
  animation: cc-fade 0.12s ease;
}
@keyframes cc-fade {
  from { opacity: 0; }
}

.cc-panel {
  width: 420px;
  max-width: 95vw;
  max-height: calc(100vh - 6rem);
  background: white;
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(0, 0, 0, 0.05);
  overflow-y: auto;
  padding: 1.5rem;
  animation: cc-slide 0.2s cubic-bezier(0.16, 1, 0.3, 1);
}
@keyframes cc-slide {
  from { opacity: 0; transform: translateX(-20px); }
  to { opacity: 1; transform: translateX(0); }
}

.cc-header {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  margin-bottom: 0.5rem;
}

.cc-back {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.375rem;
  border-radius: 6px;
  color: rgb(var(--slate-10));
  transition: all 0.12s ease;
  flex-shrink: 0;
}
.cc-back:hover {
  color: rgb(var(--slate-12));
  background: rgb(var(--slate-3));
}

.cc-title {
  font-size: 17px;
  font-weight: 700;
  color: rgb(var(--slate-12));
  margin: 0;
}

.cc-description {
  font-size: 13.5px;
  color: rgb(var(--slate-9));
  margin: 0 0 1.25rem;
  line-height: 1.5;
}

.cc-fields {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.cc-label {
  font-size: 13px;
  font-weight: 600;
  color: rgb(var(--slate-11));
  margin: 0;
}

.cc-input {
  width: 100%;
  padding: 0.625rem 0.75rem;
  font-size: 14px;
  color: rgb(var(--slate-12));
  background: white;
  border: 1px solid rgb(var(--slate-5));
  border-radius: 8px;
  outline: none;
  transition: all 0.15s ease;
}
.cc-input:focus {
  border-color: rgb(var(--blue-9));
  box-shadow: 0 0 0 2px rgba(var(--blue-4), 0.4);
}
.cc-input::placeholder {
  color: rgb(var(--slate-8));
}

/* Status chips */
.cc-status-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}
.cc-status-chip {
  padding: 0.5rem 1rem;
  border-radius: 8px;
  border: 1px solid rgb(var(--slate-5));
  font-size: 13px;
  font-weight: 500;
  color: rgb(var(--slate-11));
  background: white;
  cursor: pointer;
  transition: all 0.12s ease;
  text-transform: capitalize;
}
.cc-status-chip:hover {
  border-color: rgb(var(--blue-7));
  background: rgb(var(--blue-1));
}
.cc-status-active {
  border-color: rgb(var(--blue-9));
  background: rgb(var(--blue-2));
  color: rgb(var(--blue-11));
  font-weight: 600;
}

/* Selected labels */
.cc-selected-labels {
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
  padding: 0.625rem 0.75rem;
  border: 1px solid rgb(var(--slate-5));
  border-radius: 8px;
  min-height: 42px;
  background: white;
}

.cc-label-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.5rem;
  border-radius: 6px;
  background: rgb(var(--blue-3));
  color: rgb(var(--blue-11));
  font-size: 13px;
  font-weight: 600;
}

.cc-chip-remove {
  display: flex;
  align-items: center;
  color: rgb(var(--blue-9));
  cursor: pointer;
  padding: 0;
  margin-left: 2px;
}
.cc-chip-remove:hover {
  color: rgb(var(--ruby-11));
}

/* Search */
.cc-search-wrap {
  position: relative;
}
.cc-search-icon {
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: rgb(var(--slate-8));
  pointer-events: none;
}
.cc-search-input {
  width: 100%;
  padding: 0.5rem 0.75rem 0.5rem 2.25rem;
  font-size: 13px;
  color: rgb(var(--slate-12));
  background: white;
  border: 1px solid rgb(var(--slate-5));
  border-radius: 8px;
  outline: none;
  transition: border-color 0.15s ease;
}
.cc-search-input:focus {
  border-color: rgb(var(--blue-9));
}
.cc-search-input::placeholder {
  color: rgb(var(--slate-8));
}

/* Labels list */
.cc-labels-list {
  max-height: 220px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 2px;
  border: 1px solid rgb(var(--slate-4));
  border-radius: 8px;
  padding: 0.375rem;
}

.cc-label-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.625rem;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.1s ease;
  text-align: left;
  width: 100%;
}
.cc-label-item:hover {
  background: rgb(var(--slate-2));
}
.cc-label-item-selected {
  background: rgb(var(--blue-2));
}
.cc-label-item-selected:hover {
  background: rgb(var(--blue-3));
}

.cc-label-color {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
}

.cc-label-name {
  flex: 1;
  font-size: 13.5px;
  font-weight: 500;
  color: rgb(var(--slate-12));
}

.cc-label-check {
  flex-shrink: 0;
  color: rgb(var(--blue-11));
}

.cc-no-labels {
  text-align: center;
  padding: 1rem;
  font-size: 13px;
  color: rgb(var(--slate-9));
}

.cc-hint {
  font-size: 12.5px;
  color: rgb(var(--slate-9));
  line-height: 1.5;
  margin: 0;
}

/* No-config info box */
.cc-no-config {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  background: rgb(var(--teal-2));
  border: 1px solid rgb(var(--teal-5));
  border-radius: 8px;
}
.cc-no-config-icon {
  color: rgb(var(--teal-11));
  flex-shrink: 0;
}
.cc-no-config p {
  font-size: 13px;
  color: rgb(var(--teal-11));
  margin: 0;
}
</style>
